import { EventEmitter } from 'eventemitter3';
import { Timekeeper } from './Timekeeper';
import { Track } from './Track';
/**
 * Extracts audio frames from a MediaStreamTrack using MediaStreamTrackProcessor.
 * This is Chrome-only as MediaStreamTrackProcessor for audio is not supported in other browsers.
 */
export class ChromeAudioFrameExtractor {
    LOG_PREFIX = '[ChromeAudioFrameExtractor]';
    emitter = new EventEmitter();
    track;
    consumer;
    trackProcessor;
    abortController = new AbortController();
    stopped = false;
    outputTrack;
    // Metadata
    frameNumber = 0n;
    cumulativeTimestampMicros = 0n; // Tracks exact sample-based duration (synthetic timestamps)
    trackId;
    timestampOffsetMicros;
    constructor(track) {
        this.track = track;
        this.trackId = track.id;
        this.outputTrack = new Track(track.id, true);
    }
    start = () => {
        // Log track settings to detect sample rate mismatches
        const trackSettings = this.track.getSettings();
        console.log(`${this.LOG_PREFIX} start called`, {
            trackId: this.trackId,
            trackSampleRate: trackSettings.sampleRate,
            trackChannelCount: trackSettings.channelCount,
        });
        this.consumer = new WritableStream({
            write: (audioData) => {
                const unencodedAudioFrame = this.createUnencodedAudioFrame(audioData);
                if (this.outputTrack.hasListener()) {
                    this.outputTrack?.emit(unencodedAudioFrame, false, [
                        unencodedAudioFrame.data.buffer,
                    ]);
                }
                // AudioData must be closed after use
                audioData.close();
            },
        });
        this.trackProcessor = new MediaStreamTrackProcessor({
            track: this.track,
        });
        this.trackProcessor.readable
            .pipeTo(this.consumer, {
            signal: this.abortController.signal,
        })
            .catch((_e) => {
            console.log(`${this.LOG_PREFIX} pipeTo canceled`, {
                trackId: this.trackId,
            });
        })
            .finally(() => {
            this.stopped = true;
            console.log(`${this.LOG_PREFIX} pipeline stopped, emitting done`, {
                trackId: this.trackId,
            });
            this.outputTrack?.emit(undefined, true);
            this.emitter.emit('aborted');
        });
    };
    stop = () => {
        console.log(`${this.LOG_PREFIX} stop called`, {
            trackId: this.trackId,
        });
        if (this.stopped) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.abortController.abort('Aborted');
            this.emitter.once('aborted', () => {
                resolve();
            });
        });
    };
    getOutputTrack() {
        return this.outputTrack;
    }
    getTrackId() {
        return this.trackId;
    }
    /**
     * Create an UnencodedAudioFrame from AudioData.
     */
    createUnencodedAudioFrame(audioData) {
        if (this.timestampOffsetMicros === undefined) {
            this.timestampOffsetMicros =
                Timekeeper.getInstance('producer').getLastTimestampMicros();
            console.log(`${this.LOG_PREFIX} [FIRST_FRAME] captured timestamp offset`, {
                trackId: this.trackId.slice(0, 8),
                timestampOffsetMs: (Number(this.timestampOffsetMicros) / 1000).toFixed(1),
            });
        }
        const numberOfFrames = audioData.numberOfFrames;
        const numChannels = audioData.numberOfChannels;
        const samples = new Float32Array(numberOfFrames * numChannels);
        for (let ch = 0; ch < numChannels; ch++) {
            audioData.copyTo(samples.subarray(ch * numberOfFrames, (ch + 1) * numberOfFrames), { planeIndex: ch });
        }
        // Audio uses FIXED SYNTHETIC timestamps based on exact sample duration.
        const actualSampleRate = audioData.sampleRate;
        const durationMicros = BigInt(Math.round((numberOfFrames / actualSampleRate) * 1_000_000));
        // Use cumulative timestamp based on EXACT sample-based duration (synthetic, fixed)
        const timestampMicros = this.cumulativeTimestampMicros + this.timestampOffsetMicros;
        // Advance cumulative timestamp by EXACT sample-based duration
        this.cumulativeTimestampMicros += durationMicros;
        const unencodedAudioFrame = {
            frameNumber: this.frameNumber++,
            timestamp: timestampMicros,
            duration: durationMicros,
            trackId: this.trackId,
            data: samples,
            done: false,
            isKeyFrame: true,
        };
        return unencodedAudioFrame;
    }
}
